import React, {
  memo,
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Box,
  Button,
  Checkbox,
  Flex,
  HStack,
  IconButton,
  Input,
  Text,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { CopyIcon } from '@chakra-ui/icons';

const PAGE_SIZE = 30;

const QTestMcqRow = memo(function QTestMcqRow({
  mcqId,
  codonId,
  questionText,
  isSelected,
  onToggle,
  onCopy,
}) {
  return (
    <Box borderWidth="1px" borderRadius="md" p={3}>
      <Flex gap={3} align="start">
        <Checkbox
          mt={1}
          isChecked={isSelected}
          onChange={() => onToggle(mcqId)}
        />
        <Box flex="1" minW="0">
          <Text fontWeight="medium">{questionText || 'Untitled MCQ'}</Text>
          <Text color="gray.500" fontSize="xs" mt={1}>
            ID: {codonId || mcqId}
          </Text>
        </Box>
        <IconButton
          aria-label="Copy MCQ ID"
          icon={<CopyIcon />}
          size="sm"
          variant="ghost"
          onClick={() => onCopy(codonId || mcqId)}
        />
      </Flex>
    </Box>
  );
});

export default function QTestMcqSelector({
  mcqs,
  selectedMcqIds,
  onToggle,
  emptyText,
  helperText,
  maxHeight = '320px',
}) {
  const toast = useToast();
  const [searchTerm, setSearchTerm] = useState('');
  const [manualMcqId, setManualMcqId] = useState('');
  const [page, setPage] = useState(1);
  const deferredSearchTerm = useDeferredValue(searchTerm);

  const selectedMcqIdSet = useMemo(
    () => new Set(selectedMcqIds),
    [selectedMcqIds],
  );

  const mcqLookup = useMemo(() => {
    const lookup = new Map();

    mcqs.forEach((mcq) => {
      if (mcq?._id) {
        lookup.set(String(mcq._id).toLowerCase(), mcq);
      }

      if (mcq?.codonId) {
        lookup.set(String(mcq.codonId).toLowerCase(), mcq);
      }
    });

    return lookup;
  }, [mcqs]);

  const filteredMcqs = useMemo(() => {
    const query = deferredSearchTerm.trim().toLowerCase();

    if (!query) return mcqs;

    return mcqs.filter((mcq) => {
      const questionText = (
        mcq.question?.text ||
        mcq.questionText ||
        ''
      ).toLowerCase();
      const codonId = (mcq.codonId || '').toLowerCase();
      const mcqId = (mcq._id || '').toLowerCase();

      return (
        questionText.includes(query) ||
        codonId.includes(query) ||
        mcqId.includes(query)
      );
    });
  }, [deferredSearchTerm, mcqs]);

  const totalPages = Math.max(1, Math.ceil(filteredMcqs.length / PAGE_SIZE));
  const pagedMcqs = useMemo(() => {
    const startIndex = (page - 1) * PAGE_SIZE;
    return filteredMcqs.slice(startIndex, startIndex + PAGE_SIZE);
  }, [filteredMcqs, page]);

  useEffect(() => {
    setPage(1);
  }, [deferredSearchTerm, mcqs.length]);

  useEffect(() => {
    setPage((prev) => Math.min(prev, totalPages));
  }, [totalPages]);

  const handleCopy = useCallback(
    async (value) => {
      try {
        await navigator.clipboard.writeText(value);
        toast({
          title: 'MCQ ID copied',
          status: 'success',
          duration: 1500,
          isClosable: true,
        });
      } catch (error) {
        toast({
          title: 'Copy failed',
          description: 'Unable to copy MCQ ID',
          status: 'error',
          isClosable: true,
        });
      }
    },
    [toast],
  );

  const handleAddById = useCallback(() => {
    const normalizedInput = manualMcqId.trim().toLowerCase();

    if (!normalizedInput) {
      toast({
        title: 'Enter MCQ ID',
        description: 'Paste a Codon ID or MCQ database ID to add it',
        status: 'warning',
        isClosable: true,
      });
      return;
    }

    const matchedMcq = mcqLookup.get(normalizedInput);

    if (!matchedMcq?._id) {
      toast({
        title: 'MCQ not found',
        description: 'No QTest MCQ matched that ID',
        status: 'error',
        isClosable: true,
      });
      return;
    }

    if (selectedMcqIdSet.has(matchedMcq._id)) {
      toast({
        title: 'Already selected',
        description: 'This MCQ is already in the selection',
        status: 'info',
        isClosable: true,
      });
      return;
    }

    onToggle(matchedMcq._id);
    setManualMcqId('');
    toast({
      title: 'MCQ added',
      description: matchedMcq.codonId || matchedMcq._id,
      status: 'success',
      duration: 1800,
      isClosable: true,
    });
  }, [manualMcqId, mcqLookup, onToggle, selectedMcqIdSet, toast]);

  return (
    <>
      {helperText ? (
        <Text fontSize="sm" color="gray.500" mb={2}>
          {helperText}
        </Text>
      ) : null}

      <VStack align="stretch" spacing={3} mb={3}>
        <Input
          size="sm"
          placeholder="Search MCQ or ID"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />

        <HStack align="stretch">
          <Input
            size="sm"
            placeholder="Paste MCQ ID to add directly"
            value={manualMcqId}
            onChange={(e) => setManualMcqId(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAddById();
              }
            }}
          />
          <Button size="sm" onClick={handleAddById}>
            Add by ID
          </Button>
        </HStack>
      </VStack>

      <Text fontSize="xs" color="gray.500" mb={2}>
        {filteredMcqs.length} results | {selectedMcqIds.length} selected
      </Text>

      <Box borderWidth="1px" borderRadius="md" maxH={maxHeight} overflowY="auto" p={3}>
        {pagedMcqs.length === 0 ? (
          <Text color="gray.500" fontSize="sm">
            {emptyText}
          </Text>
        ) : (
          <VStack align="stretch" spacing={3}>
            {pagedMcqs.map((mcq) => (
              <QTestMcqRow
                key={mcq._id}
                mcqId={mcq._id}
                codonId={mcq.codonId}
                questionText={mcq.question?.text || mcq.questionText}
                isSelected={selectedMcqIdSet.has(mcq._id)}
                onToggle={onToggle}
                onCopy={handleCopy}
              />
            ))}
          </VStack>
        )}
      </Box>

      {totalPages > 1 ? (
        <HStack justify="space-between" mt={3}>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setPage((prev) => Math.max(1, prev - 1))}
            isDisabled={page === 1}
          >
            Previous
          </Button>
          <Text fontSize="xs" color="gray.500">
            Page {page} of {totalPages}
          </Text>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
            isDisabled={page === totalPages}
          >
            Next
          </Button>
        </HStack>
      ) : null}
    </>
  );
}
